import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Stack, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, Button, MenuItem, TextField, Tooltip, Chip,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import {
  useGetUserRoleQuery,
  useGetPermissionMatrixModulesQuery,
  useGetRolePermissionQuery,
  useUpdateRolePermissionMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { Loader } from "../../../Components/Loader";

const ACTIONS: Array<{ key: "view" | "create" | "edit" | "delete"; label: string; color: string }> = [
  { key: "view", label: "View", color: "#2563eb" },
  { key: "create", label: "Create", color: "#16a34a" },
  { key: "edit", label: "Edit", color: "#d97706" },
  { key: "delete", label: "Delete", color: "#dc2626" },
];

interface ModuleRow {
  module_key: string;
  prefix: string;
  display_name: string;
  category: string;
  actions: { view: number | null; create: number | null; edit: number | null; delete: number | null };
}
interface Group { category: string; modules: ModuleRow[] }
interface Flag { id: number; name: string; label: string; category: string }

const PermissionMatrix: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data: rolesResp, isLoading: rolesLoading } = useGetUserRoleQuery({} as any);
  const { data: modulesResp, isLoading: modulesLoading } = useGetPermissionMatrixModulesQuery();
  const [updateRolePermission, { isLoading: saving }] = useUpdateRolePermissionMutation();

  const roles: Array<{ id: number; roleName: string }> = (rolesResp as any)?.data ?? [];
  const groups: Group[] = (modulesResp as any)?.data?.groups ?? [];
  const flags: Flag[] = (modulesResp as any)?.data?.flags ?? [];

  const [roleId, setRoleId] = useState<number | "">("");
  const { data: rolePermResp, isFetching: rolePermLoading } = useGetRolePermissionQuery(
    { roleId } as any,
    { skip: !roleId },
  );

  // Local working set of assigned permission ids.
  const [assigned, setAssigned] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (!roleId) { setAssigned(new Set()); return; }
    const rows: any[] = (rolePermResp as any)?.data ?? [];
    setAssigned(new Set(rows.filter((p) => p.isAssigned).map((p) => p.id)));
  }, [rolePermResp, roleId]);

  // default to first role once roles load
  useEffect(() => {
    if (!roleId && roles.length) setRoleId(roles[0].id);
  }, [roles, roleId]);

  const toggle = (permId: number | null) => {
    if (!permId) return;
    setAssigned((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId); else next.add(permId);
      return next;
    });
  };

  // ids present for a given action column across all modules
  const columnIds = useMemo(() => {
    const map: Record<string, number[]> = { view: [], create: [], edit: [], delete: [] };
    groups.forEach((g) => g.modules.forEach((m) => {
      (Object.keys(map) as Array<keyof typeof map>).forEach((a) => {
        const id = (m.actions as any)[a];
        if (id) map[a].push(id);
      });
    }));
    return map;
  }, [groups]);

  const toggleColumn = (action: string) => {
    const ids = columnIds[action] || [];
    const allOn = ids.length > 0 && ids.every((id) => assigned.has(id));
    setAssigned((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const totalModules = useMemo(() => groups.reduce((n, g) => n + g.modules.length, 0), [groups]);

  const onSave = async () => {
    if (!roleId) return;
    try {
      const res: any = await updateRolePermission({ roleId, permissionIds: Array.from(assigned) }).unwrap();
      if (res?.status === false) { showErrorMessage(res.message || "Save failed"); return; }
      showMessage("Permissions updated.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Save failed");
    }
  };

  if (rolesLoading || modulesLoading) return <Loader />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card sx={{ p: 2.5, mb: 2, background: "linear-gradient(90deg,#0f3057,#16324f)", color: "#fff", display: "flex", alignItems: "center", gap: 2 }}>
        <SecurityIcon />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Privileges</Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>{totalModules} modules available in the system</Typography>
        </Box>
      </Card>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <TextField select size="small" label="Role" value={roleId}
          onChange={(e) => setRoleId(Number(e.target.value))} sx={{ minWidth: 240 }}>
          {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.roleName}</MenuItem>)}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" disabled={!roleId || saving} onClick={onSave}
          sx={{ bgcolor: "#0f3057", "&:hover": { bgcolor: "#16324f" } }}>
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </Stack>

      <Card variant="outlined">
        <TableContainer sx={{ position: "relative" }}>
          {rolePermLoading && <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.5)", zIndex: 1 }} />}
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f8fafc" } }}>
                <TableCell width={48}>#</TableCell>
                <TableCell>Module Key</TableCell>
                <TableCell>Display Name</TableCell>
                {ACTIONS.map((a) => (
                  <TableCell key={a.key} align="center" sx={{ color: a.color }}>
                    <Stack alignItems="center" spacing={0}>
                      <span>{a.label.toUpperCase()}</span>
                      <Tooltip title={`Toggle all ${a.label}`}>
                        <Checkbox size="small" sx={{ p: 0.25 }}
                          checked={(columnIds[a.key] || []).length > 0 && (columnIds[a.key] || []).every((id) => assigned.has(id))}
                          onChange={() => toggleColumn(a.key)} disabled={!roleId} />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={`cat-${g.category}`}>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ bgcolor: "#eef2f7", fontWeight: 700, color: "#334155", py: 0.75 }}>
                      {g.category}
                    </TableCell>
                  </TableRow>
                  {g.modules.map((m, i) => (
                    <TableRow key={m.prefix} hover>
                      <TableCell sx={{ color: "#94a3b8" }}>{i + 1}</TableCell>
                      <TableCell><Chip size="small" label={m.module_key} sx={{ fontFamily: "monospace", bgcolor: "#f1f5f9" }} /></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{m.display_name}</TableCell>
                      {ACTIONS.map((a) => {
                        const id = (m.actions as any)[a.key] as number | null;
                        return (
                          <TableCell key={a.key} align="center">
                            {id ? (
                              <Checkbox size="small" checked={assigned.has(id)} onChange={() => toggle(id)}
                                disabled={!roleId} sx={{ color: a.color, "&.Mui-checked": { color: a.color } }} />
                            ) : (
                              <Box component="span" sx={{ color: "#cbd5e1" }}>—</Box>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}

              {flags.length > 0 && (
                <React.Fragment>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ bgcolor: "#eef2f7", fontWeight: 700, color: "#334155", py: 0.75 }}>
                      Special Permissions
                    </TableCell>
                  </TableRow>
                  {flags.map((f, i) => (
                    <TableRow key={f.id} hover>
                      <TableCell sx={{ color: "#94a3b8" }}>{i + 1}</TableCell>
                      <TableCell><Chip size="small" label={f.name} sx={{ fontFamily: "monospace", bgcolor: "#fff7ed" }} /></TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{f.label}</TableCell>
                      <TableCell align="center">
                        <Checkbox size="small" checked={assigned.has(f.id)} onChange={() => toggle(f.id)} disabled={!roleId} />
                      </TableCell>
                      <TableCell colSpan={3} />
                    </TableRow>
                  ))}
                </React.Fragment>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default PermissionMatrix;
